import React, { Fragment, useEffect, useRef, forwardRef, useImperativeHandle, memo } from 'react';
import { useObserver, observer } from 'mobx-react-lite';
import './DemIntegration.scss';
import Icon from '@mdi/react';
import { mdiCloudUploadOutline, mdiClose } from '@mdi/js';
import EsriWidgetHost from '../../common/mapping/EsriWidgetHost/EsriWidgetHost';
import { FlatButton } from 'nrcs-design-system';
import stores from 'cd-eft/models/Stores';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { DemIntegrationVM } from './DemIntegrationVM';
import { useHistory } from 'react-router-dom';
import MapView from '@arcgis/core/views/MapView';
import { DelineateWatershedInputStateVM } from 'cd-eft/components/EFH2/DelineateWatershedInput/DelineateWatershedInputStateVM';
import { DesignContainerVM } from 'cd-eft/components/Design/DesignContainer/DesignContainerVM';
import { projectClassId } from 'cd-eft/components/Projects/ProjectContainer/ProjectContainerVM';
import { ToolBarLoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { ClippedContainerVM } from 'cd-eft/components/EFH2/ClippedDEM/ClippedContainerVM';
import { ToggleButton } from '../ToggleButton/ToggleButton';

const AppStore = stores.AppStore;
const { projectsLibraryStore: libraryStore, designStore, surveyEngineeringStore: setStore } = stores.AppStore;

export interface DemIntegrationRef {
  getVM: () => DemIntegrationVM;
}

interface IDemIntegrationComponentProps {
  demProjectVM?: DelineateWatershedInputStateVM | DesignContainerVM | ClippedContainerVM,
  vm?: DemIntegrationVM,
  mapView?: MapView,
  project_class_id?: projectClassId,
  history?: any
}

const DemIntegrationComponent = memo(forwardRef<DemIntegrationRef, IDemIntegrationComponentProps>((props, ref) => {
  const history = useHistory();
  const vmREF = useRef<DemIntegrationVM>();
  if (!vmREF.current) {
    vmREF.current = new DemIntegrationVM(props.mapView!, props.project_class_id!, props.demProjectVM!);
  }

  const vm = vmREF.current;

  // Expose the VM instance to parent components via ref
  useImperativeHandle(ref, () => ({
    getVM: () => vm
  }));

  useEffect(() => {
    if (setStore.currentTab !== 'Clipped DEM') {
      vm?.onMount(history);
      return vm.callableonUnmount;
    } else {
      MessageCenter.resetMessageCenter()
    }

  }, []);

  useEffect(() => {
    if (vm.newDEM.coordinateSystemType) {
      vm.projectionByLocation();
    }
  }, [vm.newDEM.coordinateSystemType]);

  return useObserver(() => (
    <Fragment>
      {(!vm.jobExecuting || setStore.currentTab === 'Clipped DEM') ? (
        <Fragment>
          <fieldset className="usa-fieldset p-2 mt-4">
            {setStore.currentTab !== 'Clipped DEM' && <>
              <div className="form-row-fixed-height">
                <label className="grid-col-3 usa-label pr-3" htmlFor="DEMtName">
                  DEM Name:
                </label>
                <input
                  className={vm.formValidation.formErrors.demName !== '' ? ' usa-input--error' : 'usa-input'}
                  id="demName"
                  name="demName"
                  aria-label="demName"
                  data-testid="demName"
                  placeholder="Add a DEM name...."
                  onChange={vm.setDEMName}
                  value={vm.newDEM.demName}
                  type="text"
                />
                <label className="usa-label label-with-fixed-width label-padding" htmlFor="surface-name">
                  Surface Name:
                </label>
                <input
                  className={vm.formValidation.formErrors.surfaceName !== '' ? ' usa-input--error' : 'usa-input'}
                  id="surfaceName"
                  aria-label="surfaceName"
                  name="surfaceName"
                  type="text"
                  placeholder="Add a Surface name...."
                  value={
                    setStore.isHeaderAccepted ? designStore.currentSurface.surfaceDefTitle : vm.newDEM.surfaceName
                  }
                  onChange={vm.setSurfaceName}
                  disabled={setStore.isHeaderAccepted}
                />
              </div>
              <span className="usa-error-message" id="client--message" role="alert">
                {vm.formValidation.formErrors.demName}
              </span>
              <span className="usa-error-message" id="client--message" role="alert">
                {vm.formValidation.formErrors.surfaceName}
              </span>
              <div className="form-row-fixed-height justify-content-between">
                <div className="form-row-fixed-height">
                  <label className="usa-label label-with-fixed-width" htmlFor="coordinateSystemType">
                    Coordinate System Type:
                  </label>
                  <select
                    className={vm.formValidation.formErrors.coordinateSystemType !== '' ? ' usa-select--error cst-width' : 'usa-select cst-width'}
                    name="coordinateSystemType"
                    id="coordinateSystemType"
                    aria-label="Coordinate System Type"
                    data-testid="coordinateSystemType-select"
                    value={vm.newDEM.coordinateSystemType}
                    onChange={(e) => vm.handleCoordinateSystemChange(e)}
                    disabled={vm.newDEM.coordinateSystemType === 'NAD83(2011) StatePlane'}
                  >
                    <option value=""> - select - </option>
                    {libraryStore.coordinateSystemOptions.map((el) => (
                      <option value={el.value} key={el.value} label={el.label}>
                        {el.name}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Zone */}
                <div className="form-row-fixed-height">
                  {libraryStore.pojectionByLocationData.length === 1 || libraryStore.pojectionByLocationData.length === 0 ? (
                    <>
                      <label className="usa-label label-with-fixed-width label-padding" htmlFor="zone">
                        Zone:
                      </label>
                      <input
                        className={'usa-input zone-width'}
                        id="zone"
                        aria-label="Zone"
                        name="zone"
                        type="text"
                        value={libraryStore.pojectionByLocationData.length === 1 ? libraryStore.pojectionByLocationData[0].zoneName : ''}
                        onChange={vm.handleZoneInputChange}
                        readOnly
                      />
                    </>
                  ) : (
                    <>
                      <label className="usa-label label-with-fixed-width label-padding" htmlFor="zone">
                        Zone:
                      </label>
                      <select
                        className={vm.formValidation.formErrors.zone !== '' ? ' usa-select--error zone-width' : 'usa-select zone-width'}
                        name="zone"
                        id="zone"
                        aria-label="zone"
                        data-testid="zone-select"
                        value={vm.newDEM.zone}
                        defaultValue={libraryStore.projectSpatialRefData.zone}
                        onChange={vm.handleZoneInputChange}
                      >
                        <option value=""> - select - </option>
                        {libraryStore.pojectionByLocationData.map((el) => (
                          <option value={el.zoneName} key={el.zoneName} label={el.zoneName}>
                            {el.zoneName}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                </div>
              </div>
              <span className="usa-error-message" id="client--message" role="alert">
                {vm.formValidation.formErrors['coordinateSystemType'] && (
                  <div>Coordinate System Type {vm.formValidation.formErrors['coordinateSystemType']}</div>
                )}
              </span>
              <span className="usa-error-message" id="client--message" role="alert">
                {vm.formValidation.formErrors['zone'] && (
                  <div>Zone {vm.formValidation.formErrors['zone']}</div>
                )}
              </span>
            </>}
            <SearchBar vm={vm} />
            <hr />
            {setStore.currentTab === 'Clipped DEM' ? <div>
              <AoiSketch vm={vm} />
              <div className="form-row-fixed-height justify-content-between">
                <div className="form-row-fixed-height">
                  <label className="usa-label label-with-fixed-width" htmlFor="coordinateSystemType">
                    Coordinate System Type:
                  </label>
                  <select
                    className={
                      vm.formValidation.formErrors.coordinateSystemType !== ''
                        ? 'usa-select--error cst-width'
                        : 'usa-select cst-width'
                    }
                    name="coordinateSystemType"
                    id="coordinateSystemType"
                    aria-label="Coordinate System Type"
                    data-testid="coordinateSystemType-select"
                    value={vm.newDEM.coordinateSystemType}
                    onChange={vm.handleCoordinateSystemChange}
                    disabled={vm.newDEM.coordinateSystemType === 'NAD83(2011) StatePlane'}
                  >
                    <option value=""> - select - </option>
                    {libraryStore.coordinateSystemOptionsClipDem.map((el) => (
                      <option value={el.value} key={el.value} label={el.label}>
                        {el.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div> :
              <div className="row">
                <div className="col-md-7">
                  <AoiSketch vm={vm} />
                </div>
                <div className="col-md-5">
                  <ContourIntervalSelect vm={vm} />
                </div>
                {props.project_class_id === 4 && <LayerOptionsArea vm={vm} />}
              </div>}
            <DemFieldSet vm={vm} />
          </fieldset>
          {/* Action Buttons */}
          {setStore.currentTab !== 'Clipped DEM' && <div className="flex-justify form-row-fixed-height mb-0 mt-4">
            <div></div>

            <div>
              <button className="usa-button usa-button--outline"
                onClick={vm.onClickCancelDEM}
              >
                Close
              </button>
              <button className="usa-button mr-0 dem-buttons" disabled={!designStore.hasProject() || designStore.isLoading || vm.saveButtonDisabled}
                onClick={vm.onClickSaveDEM}
              >
                Save
              </button>
            </div>

          </div>}
        </Fragment>
      ) :
        (<ToolBarLoadingSpinner
          message={vm.jobMessages[vm.jobMessages.length - 1]}
          jobExecuting={vm.jobExecuting}
        />)}
    </Fragment>
  ));
}));

export const DemIntegration = DemIntegrationComponent;


const DemFileInput = observer((props: IDemIntegrationComponentProps) => {
  return (
    <div className="row ">
      <div className="col">
        <div className="usa-file-input__target py-1">
          <div className="usa-file-input">
            <Icon path={mdiCloudUploadOutline} title="upload" size={1.5} color="#005ea2" />
            <div className="usa-file-input_instructions">
              <span className="usa-file-input__drag-text" id="usa-file-input__drag-text">
                Drag <strong>.tif file</strong> here or browse.
              </span>
            </div>

            <div className="usa-file-input__box"></div>
            <input
              id="inFile"
              data-testid="inFile"
              className="usa-file-input__input"
              type="file"
              name="file"
              aria-describedby="usa-file-input__drag-text"
              accept=".tif"
              multiple={false}
              onChange={(e) => {
                const value: File = (e.target.files as FileList)[0];
                props.vm!.setDemFile(value);
                props.vm!.disableServiceDems();
                props.vm?.setResultDem();
                props.vm?.demSubmit();
              }}
            ></input>
          </div>
        </div>
        <div className="d-inline-flex justify-content-between uploaded-file-div w-100">
          <div className="py-1 uploaded-file-name" hidden={props.vm!.in_dem_file === undefined}>
            File: <strong>{props.vm!.in_dem_file?.name}</strong>
          </div>
          <FlatButton
            iconOnly={mdiClose}
            title="Reject DEM"
            data-testid="reject_dem"
            hidden={props.vm!.in_dem_file === undefined}
            onClick={() => {
              props.vm!.saveButtonDisabled = true;
              props.vm!.clearDemGraphics();
              const oldInput = document.getElementById('inFile') as HTMLInputElement;
              oldInput.files = null;
              oldInput.value = '';
            }}
          />
        </div>
      </div>
    </div>
  );
});

const ElevationUnitsSelect = observer((props: IDemIntegrationComponentProps) => {
  return (
    <div className="row">
      <div className="col">
        <label className="select-label mr-2" htmlFor="in_dem_elevation_units">
          Elevation Units
        </label>
        <div className="select-dropdown">
          <select
            disabled={!props.vm?.in_dem_file}
            className="usa-select margin-top-0 display: inline-flex"
            name="in_dem_elevation_units"
            id="in_dem_elevation_units"
            data-testid="in_dem_elevation_units"
            value={props.vm!.in_dem_elevation_units}
            onChange={(e) => {
              const value: string = e.target.value;
              props.vm!.in_dem_elevation_units = value;
              props.vm!.disableServiceDems();
              props.vm?.setResultDem();
              props.vm?.demSubmit(); // Comment this out so it doesn't auto go to the next step if aoi defined. Assumes uploaded dems have known areas.

            }}
          >
            <option value="feet" label="feet" disabled={!designStore.hasProject()}>
              feet
            </option>
          </select>
        </div>
      </div>
    </div>
  );
});

const SearchBar = observer((props: IDemIntegrationComponentProps) => {
  return (
    <div className="row">
      <div className="col search-widget">
        <div data-testid="search_bar" className={'express-mode'}>
          <EsriWidgetHost esriWidget={props.vm!.searchBar} idAndName={'searchWidget'} />
        </div>
      </div>
    </div>
  );
});

const AoiSketch = observer((props: IDemIntegrationComponentProps) => {
  return (
    <div className="row">
      <div className="col">
        <label className="select-label" htmlFor="areaOfInterest">
          Draw Area Of Interest (AOI)
        </label>

        <div data-testid="aoi_sketch" className={'d-inline-block express-mode'}>
          <EsriWidgetHost esriWidget={props.vm!.aoiSketch} idAndName={'areaOfInterest'} showAoiArea={true} aoiArea={props.vm?.aoiArea} />
        </div>
      </div>
    </div>
  );
});

const DemFieldSet = observer((props: IDemIntegrationComponentProps) => {
  return (
    <div className="usa-fieldset">
      {setStore.currentTab !== 'Clipped DEM' ? (
        <>
          <legend className="usa-legend">Define DEM:</legend>
          <button
            className="usa-button"
            onClick={async () => {
              await props.vm?.createDemImageLayers();
              await props.vm?.tryDemServicesAvailability();
            }}
            disabled={!AppStore.userProfileStore.applicationFlags.enabledemsearch}
          >
            Reload DEM Services
          </button>
          <div className="usa-radio" key={`DEM Search`}>
            <input
              disabled={!AppStore.userProfileStore.applicationFlags.enabledemsearch}
              className="usa-radio__input usa-radio__input--tile"
              id="dem-service"
              type="radio"
              name="dem-service"
              data-testid="dem-service"
              value="service"
              checked={props.vm!.in_dem_service_selection === 'service'}
              onChange={() => {
                const oldInput = document.getElementById('inFile') as HTMLInputElement;
                if (oldInput) {
                  oldInput.files = null;
                  oldInput.value = '';
                }
                props.vm!.clearDemGraphics();
                props.vm!.saveButtonDisabled = true;
                props.vm!.in_dem_service_selection = 'service';
                props.vm!.in_dem_title = '';
                props.vm!.checkDemServicesAvailability();
              }}
            />
            <label className="usa-radio__label" htmlFor="dem-service">
              Search for available DEM Service
            </label>
          </div>
        </>
      ) : (
        <>
          {/* <button
            className="usa-button"
            onClick={async () => {
              await props.vm?.createDemImageLayers();
              await props.vm?.tryDemServicesAvailability();
              await props.vm?.tryNaipServicesAvailability();
            }}
            disabled={!AppStore.userProfileStore.applicationFlags.enabledemsearch}
          >
            Reload Service
          </button> */}
          <div className="usa-checkbox" key={`DEM Search`}>
            <input
              disabled={!AppStore.userProfileStore.applicationFlags.enabledemsearch}
              className="usa-checkbox__input usa-checkbox__input--tile"
              id="dem-service"
              type="checkbox"
              name="dem-service"
              data-testid="dem-service"
              checked={props.vm!.in_dem_service_selection === 'service'}
              onChange={(e) => {
                const checked = e.target.checked;
                if (checked) {
                  props.vm!.clearDemGraphics();
                  props.vm!.saveButtonDisabled = true;
                  props.vm!.in_dem_service_selection = 'service';
                  props.vm!.in_dem_title = '';
                  props.vm!.checkDemServicesAvailability(true);
                } else {
                  props.vm!.in_dem_service_selection = undefined;
                }
              }}
            />
            <label className="usa-checkbox__label" htmlFor="dem-service">
              Search for available DEM Service
            </label>
          </div>
        </>
      )}
      <div className="dem-service-content" key={`DEM`} hidden={props.vm?.in_dem_service_selection !== 'service'}>
        <select
          className="usa-select margin-top-0"
          name="in_stream_threshold"
          id="dem-service-resolution"
          data-testid="dem-service-resolution"
          key={`DEM`}
          disabled={!props.vm?.isDemServiceAvailable}
          onChange={(e) => {
            const value: string = e.target.value;
            props.vm!.in_dem_id = value;
            props.vm?.selectDemService();
          }}
        >
          <option value=""> - select - </option>
          {props.vm?.availableDemServices
            ? props.vm?.availableDemServices.map((el) => (
              <option
                value={el.id}
                key={el.id}
                className={el.notEligible ? 'highlightedRow usa-tooltip' : ''}
                title={el.notEligible ? MessageCenter.getFromCache('Hydro_67') : ''}
              >
                {el.notEligible ? `${el.title} \u24d8` : el.title}
              </option>
            ))
            : null}

        </select>
        <button
          className="usa-button"
          onClick={props.vm?.openDemUrl}
          disabled={!(props.vm?.isDemServiceAvailable)}
        >
          View Metadata
        </button>
        <button
          className="usa-button"
          onClick={() => {
            props.vm?.previewDem();
          }}
          disabled={!(props.vm?.isDemServiceAvailable)}
        >
          Preview DEM
        </button>
      </div>
      {setStore.currentTab !== 'Clipped DEM' &&
        <>
          <div className="usa-radio">
            <input
              className="usa-radio__input usa-radio__input--tile"
              id="dem-upload"
              data-testid="dem-upload"
              type="radio"
              name="dem-upload"
              value="upload"
              checked={props.vm!.in_dem_service_selection === 'upload'}
              onChange={() => {
                props.vm!.saveButtonDisabled = true;
                console.warn('SELECT UPLOAD DEM OPTION');
                props.vm!.in_dem_service_selection = 'upload';
                props.vm!.in_dem_title = '';
                props.vm!.in_dem_hillshade = "None";
              }}
            />
            <label className="usa-radio__label" htmlFor="dem-upload">
              Upload DEM
            </label>
          </div>
          <div hidden={props.vm?.in_dem_service_selection !== 'upload'} className="row">
            <div className="col-md-8">
              <DemFileInput vm={props.vm} />
            </div>
            <div className="col">
              <ElevationUnitsSelect vm={props.vm} demProjectVM={props.demProjectVM} />
            </div>
          </div>
        </>
      }
      {setStore.currentTab === 'Clipped DEM' && (
        <div>
          <div className="usa-checkbox">
            <input
              className="usa-checkbox__input usa-checkbox__input--tile"
              id="naip-imagery"
              type="checkbox"
              name="naip-imagery"
              checked={props.vm!.in_naip_selection === 'service'}
              // disabled={props.vm!.suppressNaipServiceLoading === true}
              onChange={(e) => {
                const checked = e.target.checked;
                if (checked) {
                  props.vm!.in_naip_selection = 'service';
                  props.vm!.checkNaipServicesAvailability();
                } else {
                  props.vm!.in_naip_selection = undefined;
                }
              }}
            />
            <label className="usa-checkbox__label" htmlFor="naip-imagery">
              NAIP Imagery
            </label>
          </div>
        </div>
      )}
    </div>
  );
});
const LayerOptionsArea = observer((props: IDemIntegrationComponentProps) => {
  return (
    <div className="col">
      <div className="row">Create the following:</div>
      <div className="row">
        <HillshadeToggle vm={props.vm!} />

        <ContoursToggle vm={props.vm} />

        <ContourIntervalSelect vm={props.vm} />
      </div>
      <div className="row">
        <DepthgridToggle vm={props.vm!} />

        <StreamlinesToggle vm={props.vm} />

        <StreamThresholdSelect vm={props.vm} />
      </div>
    </div>
  );
});
const HillshadeToggle = observer((props: IDemIntegrationComponentProps) => {
  return (
    <div>
      <ToggleButton
        className={'create-layer'}
        callback={() => {
          props.vm!.setHillshadeChoice();
        }}
        title={'Hillshade'}
        flag={props.vm!.in_generate_hillshade}
        id={'in_generate_hillshade'}
      />
    </div>
  );
});

const DepthgridToggle = observer((props: IDemIntegrationComponentProps) => {
  return (
    <div>
      <ToggleButton
        className={'create-layer'}
        callback={() => {
          props.vm!.setDepthgridChoice();
        }}
        title={'Depthgrid'}
        flag={props.vm!.in_generate_depthgrid}
        id={'in_generate_depthgrid'}
      />
    </div>
  );
});

const ContoursToggle = observer((props: IDemIntegrationComponentProps) => {
  return (
    <div>
      <ToggleButton
        className={'create-layer'}
        callback={() => {
          props.vm!.setContoursChoice();
        }}
        title={'Contours'}
        flag={props.vm!.in_generate_contours}
        id={'in_generate_contours'}
      />
    </div>
  );
});
const ContourIntervalSelect = observer((props: IDemIntegrationComponentProps) => {
  return (
    <div className="row align-items-center">
      <div className="col d-flex justify-content-end">
        <label className="select-label" htmlFor="in_contour_interval">
          Contour&nbsp;Interval&nbsp;&nbsp;
        </label>
        <select
          disabled={!props.vm!.in_generate_contours}
          className="usa-select margin-top-0 ms-2"
          name="in_contour_interval"
          id="in_contour_interval"
          data-testid="in_contour_interval"
          value={designStore.contourInterval}
          onChange={(e) => {
            const value: number = parseFloat(e.target.value);
            props.vm!.setContourInterval(value);
          }}
        >
          <option value="2" label="2.0 ft">
            2.0 ft
          </option>
          <option value="5" label="5.0 ft">
            5.0 ft
          </option>
          <option value="10" label="10.0 ft">
            10.0 ft
          </option>
        </select>
      </div>
    </div>
  );
});
const StreamlinesToggle = observer((props: IDemIntegrationComponentProps) => {
  return (
    <div>
      <ToggleButton
        flag={props.vm!.in_generate_streamlines}
        callback={() => {
          props.vm!.setStreamlinesChoice();
        }}
        id={'in_generate_streamlines'}
        className={'create-layer'}
        title={'Streamlines'}
      />
    </div>
  );
});

const StreamThresholdSelect = observer((props: IDemIntegrationComponentProps) => {
  return (
    <div className="row">
      <div className="col">
        <label className="select-label mr-1" htmlFor="in_stream_threshold">
          Stream&nbsp;Threshold&nbsp;
        </label>
      </div>
      <div className="col select-dropdown">
        <select
          disabled={props.vm!.in_generate_streamlines ? false : true}
          className="usa-select margin-top-0"
          name="in_stream_threshold"
          id="in_stream_threshold"
          data-testid="in_stream_threshold"
          value={props.vm!.in_stream_threshold}
          onChange={(e) => {
            const value: number = parseFloat(e.target.value);
            props.vm!.setStreamlineThreshold(value);
          }}
        >
          <option value="0.25">0.25 acres</option>
          <option value="0.5">0.5 acres</option>
          <option value="1">1.0 acres</option>
          <option value="2">2.0 acres</option>
          <option value="4">4.0 acres</option>
          <option value="8">8.0 acres</option>
        </select>
      </div>
    </div>
  );
});
